"use client";

import { useState } from "react";
import { FlaskConical, Pill, Search } from "lucide-react";
import { searchDrugs, type DrugEntry } from "@/src/services/drugCatalog";
import {
  catalogItemToOrder,
  searchOrderCatalog,
  type AiAuditContext,
  type CatalogItem,
  type OrderLane,
  type ParsedOrder,
} from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type LaneOrderSearchProps = {
  lane: OrderLane;
  onAddOrders: (orders: ParsedOrder[], audit?: AiAuditContext) => void;
};

/**
 * Compact drug / lab / procedure search that lives inside a single order lane.
 * Whatever is picked is added straight to THIS lane (not auto-routed), so the
 * doctor can build the one-day or continuous list in place.
 */
export function LaneOrderSearch({ lane, onAddOrders }: LaneOrderSearchProps) {
  const { t } = useLang();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const drugs = query.trim() ? searchDrugs(query, 5) : [];
  const catalog = query.trim() ? searchOrderCatalog(query, 5) : [];

  function addDrug(drug: DrugEntry) {
    const order: ParsedOrder = {
      id: `tmt-${Date.now()}`,
      category: drug.category,
      label: drug.name,
      details: drug.direction,
      lane,
      stat: false,
      source: "catalog",
      tmtId: drug.tmtId,
      quantity: drug.quantity,
    };
    onAddOrders([order], {
      action: "Drug added to lane",
      userInput: query || drug.name,
      aiSuggestion: `${drug.name} (${drug.tmtId})`,
      selectedOutput: `${drug.name} → ${lane === "continuous" ? "Continuous" : "One-Day"}`,
      evidence: `Local TMT drug catalog · ${drug.tmtId} · clinician review required`,
      model: "TMT formulary (local)",
    });
    setQuery("");
    setOpen(false);
  }

  function addCatalog(item: CatalogItem) {
    onAddOrders([catalogItemToOrder(item, lane)], {
      action: "Order added to lane",
      userInput: query || item.name,
      aiSuggestion: `${item.name} — ${item.defaultDetails}`,
      selectedOutput: `${item.name} → ${lane === "continuous" ? "Continuous" : "One-Day"}`,
      evidence: "Hospital order catalog · clinician review required",
      model: "Nong SATI catalog",
    });
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="lane-search">
      <div className="lane-search-box">
        <Search size={14} />
        <input
          value={query}
          placeholder={t("order.entry.laneSearch")}
          aria-label={t("order.entry.laneSearch")}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        />
      </div>
      {open && (drugs.length > 0 || catalog.length > 0) && (
        <div className="lane-search-results" role="listbox" aria-label="Order matches for this lane">
          {drugs.map((drug) => (
            <button
              type="button"
              key={drug.tmtId}
              className="catalog-suggestion"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => addDrug(drug)}
            >
              <span className="catalog-icon"><Pill size={13} /></span>
              <span className="catalog-copy"><strong>{drug.name}</strong><small>{drug.direction}</small></span>
            </button>
          ))}
          {catalog.map((item) => (
            <button
              type="button"
              key={item.name}
              className="catalog-suggestion"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => addCatalog(item)}
            >
              <span className="catalog-icon"><FlaskConical size={13} /></span>
              <span className="catalog-copy"><strong>{item.name}</strong><small>{item.defaultDetails}</small></span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
